import { Logger } from 'loglevel';
import { DateRangePresetCustom, DateRangePresetDays30, DateRangeValue } from './dateHelpers';

// Nature flags (bit positions)
const NATURE_FLAG_MENSUELLE = 0x0001; // 0b0000_0000_0000_0001
const NATURE_FLAG_ARRET_TRAVAIL = 0x0002; // 0b0000_0000_0000_0010
const NATURE_FLAG_FIN_CONTRAT = 0x0004; // 0b0000_0000_0000_0100
const NATURE_FLAG_REPRISE_TRAVAIL = 0x0008; // 0b0000_0000_0000_1000
const NATURE_FLAG_FCTU = 0x0400; // 0b0000_0100_0000_0000
const NATURE_FLAG_AMORCAGE = 0x0800; // 0b0000_1000_0000_0000
const NATURE_FLAG_DPAE = 0x1000; // 0b0001_0000_0000_0000

// Status flags (bit positions)
const STATUS_FLAG_EN_TRAITEMENT = 0x0010; // 0b0000_0001_0000
const STATUS_FLAG_RECUS = 0x0020; // 0b0000_0010_0000
const STATUS_FLAG_ACCEPTE = 0x0040; // 0b0000_0100_0000
const STATUS_FLAG_ERREUR = 0x0080; // 0b0000_1000_0000

// Mode flags (bit positions)
const MODE_FLAG_REEL = 0x0100; // 0b0001_0000_0000
const MODE_FLAG_TEST = 0x0200; // 0b0010_0000_0000

/**
 * Filter parameters nested inside StateParams (parsed from the JSON string).
 */
export interface StateConnectStateParams {
  filters?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  siren?: string | null;
}

/**
 * Top-level decoded structure of the stateConnect URL parameter.
 * StateParams is a JSON-encoded string containing the actual filter values.
 */
export interface StateConnectData {
  StateName?: string;
  StateParams?: string | null;
}

/**
 * Parsed filter state, ready to be applied to localStorage
 */
export interface ParsedStateConnectFilters {
  selectedNatures: string[];
  selectedStatus: string | null;
  /** null means "do not override existing mode setting" */
  isTest: boolean | null;
  dateRange: DateRangeValue | null;
  siret: string | null;
}

/**
 * Decodes a base64-encoded stateConnect URL parameter and returns the parsed DSN filter state.
 * Returns null if the parameter is invalid, cannot be parsed, or StateName is not "DsnList".
 */
export const parseStateConnect = (
  base64: string,
): { type: string; filters: ParsedStateConnectFilters | null } | null => {
  try {
    const decoded = atob(base64);
    const outer: StateConnectData = JSON.parse(decoded);
    if (!outer.StateName) return null;
    if (!outer.StateParams) return { type: outer.StateName, filters: null };
    const stateParams: StateConnectStateParams = JSON.parse(outer.StateParams);
    const parsedFilters = decodeDsnStateConnectFilters(stateParams);
    return { type: outer.StateName, filters: parsedFilters };
  } catch {
    return null;
  }
};

/**
 * Maps the numeric filter flags and dates from a StateConnectStateParams object to
 * the DSN filter keys used by the application.
 */
export const decodeDsnStateConnectFilters = (
  data: StateConnectStateParams,
): ParsedStateConnectFilters => {
  const filterValue = typeof data.filters === 'number' ? data.filters : 0;

  // Nature flags → nature key array
  const selectedNatures: string[] = [];
  if (filterValue & NATURE_FLAG_MENSUELLE) selectedNatures.push('DsnMensuelle');
  if (filterValue & NATURE_FLAG_ARRET_TRAVAIL) selectedNatures.push('DsnArretTravail');
  if (filterValue & NATURE_FLAG_FIN_CONTRAT) selectedNatures.push('DsnFinContrat');
  if (filterValue & NATURE_FLAG_REPRISE_TRAVAIL) selectedNatures.push('DsnRepriseTravail');
  if (filterValue & NATURE_FLAG_FCTU) selectedNatures.push('DsnFCTU');
  if (filterValue & NATURE_FLAG_AMORCAGE) selectedNatures.push('DsnAmorcage');
  if (filterValue & NATURE_FLAG_DPAE) selectedNatures.push('DsnDpae');

  // Status flags → single status group key (UI supports one status group at a time)
  let selectedStatus: string | null = null;
  if (filterValue & STATUS_FLAG_EN_TRAITEMENT) selectedStatus = 'StatusInTreatment';
  else if (filterValue & STATUS_FLAG_RECUS) selectedStatus = 'StatusReceipts';
  else if (filterValue & STATUS_FLAG_ACCEPTE) selectedStatus = 'StatusAccepted';
  else if (filterValue & STATUS_FLAG_ERREUR) selectedStatus = 'StatusError';

  // Mode flags → isTest boolean (null when neither flag is set)
  const isTestFlagSet = !!(filterValue & MODE_FLAG_TEST);
  const isReelFlagSet = !!(filterValue & MODE_FLAG_REEL);
  let isTest: boolean | null = null;
  if (isTestFlagSet && !isReelFlagSet) {
    isTest = true;
  } else if (isReelFlagSet && !isTestFlagSet) {
    isTest = false;
  }

  // Date range
  let dateRange: DateRangeValue | null = null;
  if (data.startDate || data.endDate) {
    dateRange = {
      preset: DateRangePresetCustom,
      start: data.startDate ?? null,
      end: data.endDate ?? null,
    };
  }

  return {
    selectedNatures,
    selectedStatus,
    isTest,
    dateRange,
    siret: data.siren ?? null,
  };
};

/**
 * Applies the parsed DSN stateConnect filters to localStorage so they are picked up
 * by the DSN list page after authentication.
 *
 * All existing DSN filter values are replaced (not preserved). Keys that are not
 * specified in the stateConnect are reset to their application defaults.
 *
 * Keys used match those defined in useDsnListLocalStorage.ts:
 *   'dsn-natures-filter', 'dsn-status-filter', 'dsn-test-mode',
 *   'dsn-date-range', 'dsn-search-value', 'dsn-user-filter', 'dsn-current-page'
 */
export const applyDsnStateConnectToLocalStorage = (filters: ParsedStateConnectFilters): void => {
  localStorage.setItem('dsn-natures-filter', JSON.stringify(filters.selectedNatures));
  localStorage.setItem('dsn-status-filter', JSON.stringify(filters.selectedStatus));
  localStorage.setItem('dsn-test-mode', JSON.stringify(filters.isTest ?? true));
  localStorage.setItem(
    'dsn-date-range',
    JSON.stringify(filters.dateRange ?? { preset: DateRangePresetDays30 }),
  );
  localStorage.setItem('dsn-search-value', JSON.stringify(filters.siret ?? ''));
  localStorage.removeItem('dsn-user-filter');
  localStorage.setItem('dsn-current-page', JSON.stringify(1));
};

/**
 * Resolves a redirect from the stateConnect URL parameter.
 * Returns 'DsnList' if the stateConnect parameter is valid and contains DsnList filters.
 * Returns null if the parameter is invalid or contains no valid DsnList filters.
 */
export const resolveRedirectFromStateConnect = (
  stateConnect: string | null,
  logger: Logger,
): 'DsnList' | null => {
  if (!stateConnect) {
    return null;
  }

  logger.info('📊 Processing stateConnect filter parameter');
  const result = parseStateConnect(stateConnect);
  if (!result) {
    logger.warn('⚠️ Failed to parse stateConnect parameter, ignoring');
    return null;
  }

  const { type, filters } = result;
  if (type === 'DsnList' && filters) {
    applyDsnStateConnectToLocalStorage(filters);
    logger.info('✅ stateConnect filters applied to localStorage');
    return 'DsnList';
  }

  if (type === 'DsnList') {
    // DsnList sans filtres → on redirige quand même mais sans filtres
    logger.warn('⚠️ stateConnect DsnList without valid filters, redirecting without filters');
    return 'DsnList';
  }

  return null;
};
